import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { AbcCore } from '@barthauer/abc/core';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, takeUntil, tap } from 'rxjs/operators';
import { IWizardImportPageComponent, WizardImportConfig } from './models/config.model';
import { WorkerResult, WorkerState } from './models/import.model';
import { DataService } from './services/data.service';
import { WizardImportService } from './wizard-import.service';

@Component({
    selector: 'abc-wizard-import',
    templateUrl: './wizard-import.component.html',
    styleUrls: ['./wizard-import.component.scss'],
    standalone: false
})
export class WizardImportComponent implements OnInit, OnDestroy {
  @Output() wizardCloseClick: EventEmitter<void> = new EventEmitter<void>();

  public importConfig: WizardImportConfig;
  public progressPercentage: number;
  public progressMessage: string;
  public retryButtonCaption: string;
  public processState: ProcessState;
  public ProcessState = ProcessState;
  public progressStatusFormat: (ratio: number, value: number) => string;
  private subscriptions = new Array<Subscription>();
  private importSubject: Subject<void> = new Subject();
  private downloadSubject: Subject<void> = new Subject();
  private stopGettingCurrentStateSubject = new Subject<WorkerResult>();
  private cancelImportSubject: Subject<void> = new Subject();

  constructor(
    importService: WizardImportService,
    private dataService: DataService,
    private abcCore: AbcCore,
    private messageBoxService: MessageBoxService
  ) {
    importService.onLoadWizard().subscribe((config: WizardImportConfig) => (this.importConfig = config));

    this.updateProgress(0, ProcessState.BeforeFinishClick);

    this.progressStatusFormat = (ratio, value) => {
      const percent: number = ratio * 100;
      const percentText: string = percent.toFixed(0);
      let text: string = 'Fortschritt: ' + percentText + '%';
      if (this.progressMessage) {
        text += ' ' + this.progressMessage;
      }
      return text;
    };

    let currentWorkerResult: WorkerResult;
    this.subscriptions.push(
      this.importSubject
        .asObservable()
        .pipe(
          concatMap(() => {
            this.updateProgress(0, ProcessState.Importing, 'Starte Import...');
            return this.importConfig?.startImportWorker(this.importConfig.transferObject).pipe(
              concatMap((result) => {
                currentWorkerResult = result;
                return timer(0, 2000).pipe(
                  takeUntil(
                    this.stopGettingCurrentStateSubject.pipe(
                      concatMap((r) => {
                        if (r.state === WorkerState.CompletedSuccessfully) {
                          let msg = r.data.message;
                          if (!msg) msg = 'Der Vorgang wurde erfolgreich ausgeführt.';
                          this.updateProgress(100, ProcessState.Done, msg);
                          return of(currentWorkerResult);
                        } else if (r.state === WorkerState.Cancelled) {
                          this.updateProgress(0, ProcessState.Cancelled, 'Der Vorgang wurde abgebrochen.');
                          return of(null);
                        } else {
                          return of(null);
                        }
                      })
                    )
                  ),
                  concatMap(() => {
                    this.updateProgressWorkerResult(result);
                    return dataService.getExtensionWorkersCurrentState(result).pipe(
                      tap((nextResult) => {
                        result = nextResult;
                        currentWorkerResult = nextResult;
                        this.updateProgressWorkerResult(result);
                        if (
                          result.state === WorkerState.CompletedSuccessfully ||
                          result.state === WorkerState.Cancelled ||
                          result.state === WorkerState.Faulted
                        ) {
                          this.stopGettingCurrentStateSubject.next(result);
                        }
                      }),
                      catchError((error) => {
                        this.stopGettingCurrentStateSubject.next(result);
                        return this.handleError(error);
                      })
                    );
                  }),
                  catchError((error) => this.handleError(error))
                );
              }),
              catchError((error) => this.handleError(error))
            );
          }),
          catchError((error) => this.handleError(error))
        )
        .subscribe(
          () => {},
          (error) => this.updateProgress(100, ProcessState.Error, error)
        )
    );

    this.subscriptions.push(
      this.cancelImportSubject
        .asObservable()
        .pipe(
          concatMap(() => {
            this.stopGettingCurrentStateSubject.next(new WorkerResult({ state: WorkerState.Cancelled }));
            return this.dataService
              .cancelExtensionWorker(currentWorkerResult)
              .pipe(catchError((error) => this.handleError(error)));
          }),
          catchError((error) => this.handleError(error))
        )
        .subscribe(
          () => {},
          (error) => this.updateProgress(100, ProcessState.Error, error)
        )
    );
  }

  private handleError(error: any): Observable<Error> {
    this.updateProgress(100, ProcessState.Error, error);
    return of(error);
  }

  public stepperSelectionChange(stepper: MatStepper, event: StepperSelectionEvent) {
    if (event.selectedIndex !== 0) {
      //affects only content pages
      if (event.selectedIndex !== stepper.steps.length - 1) {
        event.selectedStep.completed = false;
      } else {
        //last/final page
        this.updateProgress(0, ProcessState.BeforeFinishClick);
      }
    }
  }
  private updateProgressWorkerResult(workerResult: WorkerResult) {
    if (workerResult.data) {
      this.updateProgress(workerResult.data.progress, ProcessState.Importing, workerResult.data.message);
    }
  }
  private updateProgress(percentage: number, processState: ProcessState, textOrError?: any) {
    if (textOrError) {
      if (typeof textOrError === 'string' || textOrError instanceof String) {
        this.progressMessage = textOrError.toString();
      } else if (textOrError instanceof Error || textOrError instanceof HttpErrorResponse) {
        this.progressMessage = 'Fehler: ' + this.abcCore.error.getErrorInfo(textOrError).message;
      }
    } else {
      this.progressMessage = '';
    }

    //HACK: To update progress message when no progress percentage has changed
    if (this.progressPercentage !== undefined && this.progressPercentage === percentage) {
      this.progressPercentage += 0.001;
    } else {
      this.progressPercentage = percentage;
    }

    this.processState = processState;
    switch (this.processState) {
      case ProcessState.Cancelled:
        this.retryButtonCaption = 'Start';
        break;
      case ProcessState.Importing:
        this.retryButtonCaption = 'Abbrechen';
        break;
      case ProcessState.Done:
        this.retryButtonCaption = 'Schließen';
        break;
      case ProcessState.Error:
        this.retryButtonCaption = 'Erneut versuchen';
        break;
    }
  }

  private getImportFileName(): string {
    return this.importConfig.importFileNameSchema.replace(
      WizardImportConfig.TimeStamp_Placeholder,
      this.getTimeStamp()
    );
  }

  private getTimeStamp(): string {
    const currentDate = new Date();
    return (
      currentDate.getFullYear() +
      ('0' + (currentDate.getMonth() + 1)).slice(-2) +
      ('0' + currentDate.getDate()).slice(-2) +
      ('0' + currentDate.getHours()).slice(-2) +
      ('0' + currentDate.getMinutes()).slice(-2) +
      ('0' + currentDate.getSeconds()).slice(-2)
    );
  }

  ngOnInit() {}
  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  public moveStep(stepper: MatStepper, component: IWizardImportPageComponent): void {
    if (component.isValid) {
      component.onNext();
      stepper.selected.completed = true;
      stepper.next();
    } else if (component.invalidMessage) {
      this.showMessageBox(component.invalidMessage);
    }
  }

  private showMessageBox(htmlMsg: string) {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.icon = MessageBoxIcon.Warning;
    settings.button = MessageBoxButtons.OK;

    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;

    this.subscriptions.push(this.messageBoxService.showMessageBox(settings).subscribe());
  }

  public finishClick(): void {
    this.importSubject.next(null);
  }

  public retryButtonClick(): void {
    switch (this.processState) {
      case ProcessState.Cancelled:
      case ProcessState.Error:
        this.importSubject.next(null);
        break;
      case ProcessState.Importing:
        this.cancelImportSubject.next(null);
        break;
      case ProcessState.Done:
        this.downloadSubject.next(null);
        this.wizardCloseClick.emit();
        break;
    }
  }
}

enum ProcessState {
  BeforeFinishClick = 1,
  Importing,
  Cancelled,
  Done,
  Error,
}
